import React, { useState, useEffect } from "react";
import { FaPlus, FaEdit, FaTrash, FaClipboardCheck } from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import { useBuilding } from "../../context/BuildingContext";
import { db } from "../../firebase";
import { useToast } from "../../components/Toast";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
  inputBase,
  pageContainer,
  pageTitle,
  tableHeader,
  tableCell,
  modalOverlay,
} from "../../utils/designTokens";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";

const STATUS_OPTIONS = ["Programada", "Completada"];

const emptyForm = {
  title: "",
  company: "",
  date: "",
  status: "Programada",
};

const RevisionesAdmin = ({ setSelected }) => {
  const { buildings, selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const buildingsToProcess = selectedBuilding
    ? buildings.filter((b) => b.buildingid === selectedBuilding.buildingid)
    : buildings;

  const fetchItems = async () => {
    if (!buildings || buildings.length === 0) { setLoading(false); return; }
    setLoading(true);
    try {
      let all = [];
      for (const b of buildingsToProcess) {
        const q = query(
          collection(db, "inspections"),
          where("buildingid", "==", Number(b.buildingid))
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          buildingName: b.name,
        }));
        all = all.concat(data);
      }
      all.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
      setItems(all);
    } catch (error) {
      console.error("Error al traer revisiones:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [selectedBuilding, buildings]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditing(item);
    const d = item.date?.seconds
      ? new Date(item.date.seconds * 1000).toISOString().slice(0, 10)
      : "";
    setForm({
      title: item.title || "",
      company: item.company || "",
      date: d,
      status: item.status || "Programada",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedBuilding) { showToast("Selecciona un edificio primero", "warning"); return; }
    if (!form.title.trim()) { showToast("El título es obligatorio", "error"); return; }
    if (!form.date) { showToast("La fecha es obligatoria", "error"); return; }

    setSaving(true);
    try {
      const payload = {
        buildingid: Number(selectedBuilding.buildingid),
        title: form.title.trim(),
        company: form.company.trim(),
        date: Timestamp.fromDate(new Date(form.date + "T12:00:00")),
        status: form.status,
      };

      if (editing) {
        await updateDoc(doc(db, "inspections", editing.id), payload);
      } else {
        await addDoc(collection(db, "inspections"), payload);
      }

      setShowModal(false);
      setForm(emptyForm);
      setEditing(null);
      fetchItems();
    } catch (error) {
      console.error("Error al guardar revisión:", error);
      showToast("Error al guardar: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "inspections", id));
      setDeleteConfirm(null);
      fetchItems();
    } catch (error) {
      console.error("Error al eliminar revisión:", error);
      showToast("Error al eliminar: " + error.message, "error");
    }
  };

  const toDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div style={styles.headerTitleSection}>
          <h2 style={styles.pageTitle}>
            <FaClipboardCheck style={{ marginRight: 8 }} />
            Revisiones
          </h2>
          <p style={styles.pageSubtitle}>
            {selectedBuilding
              ? `${selectedBuilding.name} · ${items.length} revisiones`
              : `${buildingsToProcess.length} edificio${buildingsToProcess.length > 1 ? "s" : ""} · ${items.length} revisiones`}
          </p>
        </div>
        <button
          style={{
            ...styles.addBtn,
            opacity: !selectedBuilding ? 0.5 : 1,
            cursor: !selectedBuilding ? "not-allowed" : "pointer",
          }}
          disabled={!selectedBuilding}
          onClick={handleOpenCreate}
        >
          <FaPlus size={14} /> Nueva Revisión
        </button>
      </div>

      {!selectedBuilding && (
        <div style={styles.selectBuildingNotice}>
          <p>Selecciona un edificio desde el filtro superior para ver y crear revisiones.</p>
        </div>
      )}

      {loading && selectedBuilding ? (
        <div style={styles.selectBuildingNotice}><p>Cargando...</p></div>
      ) : selectedBuilding && items.length === 0 ? (
        <div style={styles.emptyState}>
          <FaClipboardCheck size={40} color={colors.gray300} />
          <p style={styles.emptyText}>No hay revisiones para este edificio.</p>
          <button style={styles.emptyBtn} onClick={handleOpenCreate}>
            <FaPlus size={12} /> Crear primera revisión
          </button>
        </div>
      ) : selectedBuilding ? (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Título</th>
                <th style={styles.th}>Empresa</th>
                <th style={styles.th}>Fecha</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={styles.tr}>
                  <td style={styles.td}>{item.title}</td>
                  <td style={styles.td}>{item.company || "-"}</td>
                  <td style={styles.td}>{toDate(item.date)}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      backgroundColor: item.status === "Completada" ? colors.successLight : colors.infoLight,
                      color: item.status === "Completada" ? colors.successDark : colors.infoDark,
                    }}>
                      {item.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <button style={styles.iconBtn} onClick={() => handleOpenEdit(item)} title="Editar">
                        <FaEdit color={colors.primary} />
                      </button>
                      {deleteConfirm === item.id ? (
                        <div style={styles.confirmDelete}>
                          <span style={{ fontSize: typography.sizes.sm, color: colors.errorDark }}>¿Eliminar?</span>
                          <button style={styles.confirmYes} onClick={() => handleDelete(item.id)}>Sí</button>
                          <button style={styles.confirmNo} onClick={() => setDeleteConfirm(null)}>No</button>
                        </div>
                      ) : (
                        <button style={styles.iconBtn} onClick={() => setDeleteConfirm(item.id)} title="Eliminar">
                          <FaTrash color={colors.errorDark} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <AdminModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Editar Revisión" : "Nueva Revisión"}
        size="medium"
        footer={
          <>
            <button style={styles.cancelBtn} onClick={() => setShowModal(false)} disabled={saving}>
              Cancelar
            </button>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : editing ? "Actualizar" : "Crear"}
            </button>
          </>
        }
      >
        <div style={styles.buildingInfo}>
          <span style={styles.buildingLabel}>Edificio:</span>
          <span style={styles.buildingName}>{selectedBuilding?.name}</span>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Título *</label>
          <input
            type="text"
            style={styles.input}
            placeholder="Ej: Inspección de ascensores"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Empresa</label>
          <input
            type="text"
            style={styles.input}
            placeholder="Nombre de la empresa"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Fecha *</label>
          <input
            type="date"
            style={styles.input}
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Estado</label>
          <select
            style={styles.input}
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </AdminModal>
    </div>
  );
};

// Estilos migrados a design tokens
const styles = {
  page: {
    ...pageContainer,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    ...pageTitle,
    fontSize: typography.sizes.xl,
    display: "flex",
    alignItems: "center",
  },
  pageSubtitle: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
  },
  addBtn: {
    ...btnPrimary,
  },
  // Aviso de selección de edificio — sin borde, solo sombra
  selectBuildingNotice: {
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    padding: spacing[10],
    textAlign: "center",
    color: colors.gray600,
    fontSize: typography.sizes.md,
  },
  // Estado vacío — sin borde, solo sombra
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    gap: spacing[3],
  },
  emptyText: {
    color: colors.gray600,
    fontSize: typography.sizes.md,
    margin: 0,
  },
  emptyBtn: {
    ...btnPrimary,
    marginTop: spacing[2],
  },
  // Contenedor de tabla — sin borde, solo sombra
  tableWrapper: {
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray600,
    borderBottom: `1px solid ${colors.gray200}`,
    background: colors.gray50,
  },
  tr: {
    borderBottom: `1px solid ${colors.gray100}`,
  },
  td: {
    ...tableCell,
  },
  badge: {
    padding: "4px 10px",
    borderRadius: radius.md,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },
  iconBtn: {
    ...btnGhost,
    padding: 6,
    borderRadius: 6,
  },
  confirmDelete: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  confirmYes: {
    background: colors.error,
    color: colors.white,
    border: "none",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    cursor: "pointer",
  },
  confirmNo: {
    background: colors.gray200,
    color: colors.gray900,
    border: "none",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    cursor: "pointer",
  },
  buildingInfo: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    marginBottom: spacing[5],
    padding: "12px 16px",
    background: colors.primarySubtle,
    borderRadius: radius.sm,
  },
  buildingLabel: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
  },
  buildingName: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.primary,
  },
  formGroup: {
    marginBottom: spacing[4],
  },
  label: {
    display: "block",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
    marginBottom: 6,
  },
  input: {
    ...inputBase,
    boxSizing: "border-box",
  },
  cancelBtn: {
    ...btnSecondary,
  },
  saveBtn: {
    ...btnPrimary,
  },
};

export default RevisionesAdmin;
